import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/lib/i18n";
import { toast } from "sonner";
import { FileDown, Sparkles, CheckCircle2, ArrowRight } from "lucide-react";
import { API_BASE } from "@/lib/api";

export default function TryLiveDemo() {
    const { t, lang } = useI18n();
    const [name, setName] = useState("");
    const [order, setOrder] = useState("");
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e?.preventDefault?.();
        if (!name.trim() || !order.trim()) return;
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/public/demo-pdf`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    full_name: name.trim(),
                    order_number: order.trim(),
                    language: lang,
                }),
            });
            if (!res.ok) throw new Error("bad_response");
            const ref = res.headers.get("X-Demo-Ref") || "DEMO";
            const blob = await res.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `widerruf-demo-${ref}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            toast.success(t("try_live_success"));
            setDone(true);
        } catch (err) {
            toast.error(t("try_live_error"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="try-live" className="border-t border-gray-200 bg-slate-50" data-testid="try-live-section">
            <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-12 gap-10 items-start">
                {/* Left: pitch */}
                <motion.div
                    className="lg:col-span-5"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                >
                    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#0b905d]/10 text-[#0b905d] text-xs font-mono uppercase tracking-widest mb-5">
                        <Sparkles className="w-3.5 h-3.5" />
                        {t("try_live_eyebrow")}
                    </div>
                    <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-[#00153f] leading-tight">
                        {t("try_live_title")}
                    </h2>
                    <p className="mt-4 text-base text-gray-600 leading-relaxed">
                        {t("try_live_sub")}
                    </p>

                    <ol className="mt-8 space-y-3" aria-label="steps">
                        {[
                            { n: 1, label: t("try_live_step1") },
                            { n: 2, label: t("try_live_step2") },
                            { n: 3, label: t("try_live_step3") },
                        ].map((s) => (
                            <li key={s.n} className="flex items-center gap-3 text-sm">
                                <span className="flex items-center justify-center h-7 w-7 rounded-full bg-[#00153f] text-white font-mono text-xs">
                                    {s.n}
                                </span>
                                <span className="text-[#00153f] font-medium">{s.label}</span>
                            </li>
                        ))}
                    </ol>
                </motion.div>

                {/* Right: widget card */}
                <motion.div
                    className="lg:col-span-7"
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.1 }}
                >
                    <div className="rounded-lg border border-gray-200 bg-white shadow-sm overflow-hidden">
                        <div className="border-b border-gray-200 px-5 py-3 bg-slate-50 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <FileDown className="w-4 h-4 text-[#0b905d]" />
                                <span className="text-xs font-mono uppercase tracking-widest text-gray-500">widerruf-demo.pdf</span>
                            </div>
                            <div className="flex gap-1">
                                <span className="w-2 h-2 rounded-full bg-gray-300" />
                                <span className="w-2 h-2 rounded-full bg-gray-300" />
                                <span className="w-2 h-2 rounded-full bg-[#0b905d]" />
                            </div>
                        </div>

                        <form onSubmit={submit} className="p-6 sm:p-8 space-y-5" data-testid="try-live-form">
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">
                                        {t("try_live_name")}
                                    </Label>
                                    <Input
                                        required
                                        minLength={2}
                                        maxLength={120}
                                        autoComplete="name"
                                        value={name}
                                        onChange={(e) => { setName(e.target.value); setDone(false); }}
                                        placeholder="Max Mustermann"
                                        className="mt-2 h-11"
                                        data-testid="try-live-name"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-mono uppercase tracking-widest text-gray-600">
                                        {t("try_live_order")}
                                    </Label>
                                    <Input
                                        required
                                        maxLength={60}
                                        value={order}
                                        onChange={(e) => { setOrder(e.target.value); setDone(false); }}
                                        placeholder="ORD-12345"
                                        className="mt-2 h-11"
                                        data-testid="try-live-order"
                                    />
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                                <span className="text-[11px] font-mono uppercase tracking-widest text-gray-500">
                                    {t("try_live_hint")}
                                </span>
                                <Button
                                    type="submit"
                                    disabled={loading || !name.trim() || !order.trim()}
                                    className="bg-[#0b905d] hover:bg-[#067a4d] text-white h-11 px-6"
                                    data-testid="try-live-submit"
                                >
                                    <FileDown className="w-4 h-4 mr-2" />
                                    {loading ? t("try_live_cta_loading") : t("try_live_cta")}
                                </Button>
                            </div>

                            {done && (
                                <motion.div
                                    initial={{ opacity: 0, y: -4 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className="flex items-center justify-between gap-3 border border-[#0b905d]/30 bg-[#0b905d]/5 rounded-md px-4 py-3"
                                    data-testid="try-live-success"
                                >
                                    <div className="flex items-center gap-2 text-sm text-[#00153f]">
                                        <CheckCircle2 className="w-4 h-4 text-[#0b905d]" />
                                        {t("try_live_success")}
                                    </div>
                                    <Link to="/register" data-testid="try-live-register">
                                        <Button size="sm" variant="ghost" className="text-[#0b905d] hover:bg-[#0b905d] hover:text-white h-8">
                                            {t("nav_register")} <ArrowRight className="ml-1 w-3.5 h-3.5" />
                                        </Button>
                                    </Link>
                                </motion.div>
                            )}
                        </form>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
